'use client';

import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ArrowRight, CheckCircleRounded } from '@mui/icons-material';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  CascadingHoverMenus,
  CascadingMenuItem,
} from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, translate } from '@prismo-io/router';
import {
  LanguageCode,
  type LanguageCodeT,
  UserAccessType,
  type UserAccessTypeT,
} from '@prismo-io/schemas';
import { useFragment } from '@prismo-io/utils/client/use-fragment';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type FC, useMemo } from 'react';

type Props = {
  userUrl: string;
  access: UserAccessTypeT;
  order?: number;
  isCompleted?: boolean;
  disabled?: boolean;
};

export const QuarkItem: FC<Props> = (props: Props) => {
  const {
    userUrl,
    isCompleted = false,
    access,
    disabled = false,
    order = 3,
  } = props;

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  const me = useMe();

  const isMe = me && me.url === userUrl;

  const pathname = usePathname();
  const fragment = useFragment();

  const baseUrl = useMemo(
    () =>
      translate({
        app: Application.APP,
        path: '/profil/:user/competences-quark',
        params: {
          user: userUrl,
        },
        locale: locale as LanguageCodeT,
      }),
    [locale, userUrl]
  );

  const selected = useMemo(() => {
    return pathname.startsWith(baseUrl);
  }, [baseUrl, pathname]);

  return (
    <CascadingHoverMenus
      variant="popover"
      cascadingMenuProps={{
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      }}
      button={(props) => (
        <MenuItem
          {...props}
          component={Link}
          href={baseUrl}
          selected={selected}
          sx={{ order, paddingY: 2 }}
          disabled={(!isCompleted && !isMe) || disabled}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            {isCompleted && (
              <CheckCircleRounded color="primary" fontSize="small" />
            )}
            {!isCompleted && (
              <Box
                component="div"
                borderRadius={9999}
                width="24px"
                height="24px"
                display="flex"
                alignItems="center"
                justifyItems="center"
                color="primary.main"
                sx={{
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  borderColor: 'primary.main',
                }}
              >
                <Typography
                  sx={{ flex: 1 }}
                  color="primary"
                  fontSize="small"
                  fontWeight="bolder"
                  textAlign="center"
                >
                  {order}
                </Typography>
              </Box>
            )}
          </ListItemIcon>
          <ListItemText sx={{ flex: 1 }}>
            <Typography display="inline" fontWeight="bolder">
              Quark |{' '}
            </Typography>
            <Typography display="inline">
              <Trans>Compétences</Trans>
            </Typography>
          </ListItemText>
          <ListItemIcon sx={{ minWidth: '12px !important' }}>
            <ArrowRight fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      )}
      popupId="quark-menu"
    >
      {access !== UserAccessType.Enum.VISITOR && (
        <CascadingMenuItem
          component={Link}
          href={`${baseUrl}#quark-1`}
          selected={'#quark-1' === fragment}
          disabled={!isCompleted}
        >
          <Trans>Parcours professionnel</Trans>
        </CascadingMenuItem>
      )}

      {access !== UserAccessType.Enum.TALENT && (
        <CascadingMenuItem
          component={Link}
          href={`${baseUrl}#quark-2`}
          selected={'#quark-2' === fragment}
          disabled={!isCompleted}
        >
          <Trans>Compétences techniques développées</Trans>
        </CascadingMenuItem>
      )}

      {/* <Link passHref legacyBehavior href={`${baseUrl}#quark-3`}>
        <CascadingMenuItem
          component="a"
          selected={'#quark-3' === fragment}
          disabled={!isCompleted}
        >
          <Trans>Formations</Trans>
        </CascadingMenuItem>
      </Link>

      <Link passHref legacyBehavior href={`${baseUrl}#quark-4`}>
        <CascadingMenuItem
          component="a"
          selected={'#quark-4' === fragment}
          disabled={!isCompleted}
        >
          <Trans>Domaines</Trans>
        </CascadingMenuItem>
      </Link> */}
    </CascadingHoverMenus>
  );
};
